import { Vector3 } from 'three';

import { Skeleton } from '../../skeleton/core';
import {
  ROLE_TERMS,
  isArmLikeName,
  isClavicleLikeName,
  isHeadLikeName,
  isLegLikeName,
  isNeckLikeName,
  isPelvisLikeName,
  isSpineLikeName,
  isStructuralArmAuxiliaryName,
  isTerminalHelperLikeName,
} from '../../schema/mapping';
import type {
  SkeletonSpatialData,
  SpatialLandmarks,
} from '../spatial';
import {
  findLateralSpreadChain,
  getLateralSpread,
  isClavickleLike,
} from '../spatial';
import type { RecognizerConfig } from '../config';

// ─── Chain helpers (inlined from chains module) ──────────────────────────

function chainLength(skel: Skeleton, chain: number[]): number {
  if (chain.length < 2) return 0.0;
  let length = 0.0;
  for (const idx of chain) length += skel.bone(idx).length;
  return length;
}

function extractPathsToLeaves(
  skel: Skeleton,
  startIdx: number,
  ignoreAux = true,
): number[][] {
  const paths: number[][] = [];

  function getEffectiveChildren(idx: number): number[] {
    const bone = skel.bone(idx);
    const result: number[] = [];
    for (const c of bone.children) {
      if (ignoreAux && skel.bone(c).auxiliary) {
        result.push(...getEffectiveChildren(c));
      } else {
        result.push(c);
      }
    }
    return result;
  }

  function dfs(idx: number, current: number[]): void {
    const bone = skel.bone(idx);
    if (ignoreAux && bone.auxiliary) return;
    current.push(idx);
    const children = getEffectiveChildren(idx);
    if (children.length === 0) {
      paths.push([...current]);
    } else {
      for (const child of children) dfs(child, current);
    }
    current.pop();
  }

  dfs(startIdx, []);
  return paths;
}

// ─── Position helpers ────────────────────────────────────────────────────

function boneWorldPos(skel: Skeleton, idx: number): Vector3 {
  const e = skel.bone(idx).restWorld.elements;
  return new Vector3(e[12], e[13], e[14]);
}

function getRawX(skel: Skeleton, boneIdx: number, lateralAxis: Vector3): number {
  const e = skel.bone(boneIdx).restWorld.elements;
  return e[12] * lateralAxis.x + e[13] * lateralAxis.y + e[14] * lateralAxis.z;
}

function dotAbs(a: Vector3, b: Vector3): number {
  return Math.abs(a.x) * Math.abs(b.x) + Math.abs(a.y) * Math.abs(b.y) + Math.abs(a.z) * Math.abs(b.z);
}

export function shouldRetainNamedLowerArmOnExtendedChain(
  skel: Skeleton,
  armChain: number[],
  lowerArmIdx: number | null,
  lowerArmMatchedByName: boolean,
): boolean {
  if (armChain.length <= 4) return lowerArmIdx !== null;
  if (lowerArmIdx === null || !lowerArmMatchedByName) return false;

  return armChain
    .slice(1, -1)
    .filter((idx) => idx !== lowerArmIdx)
    .every((idx) => isStructuralArmAuxiliaryName(skel.bone(idx).name));
}

// ─── _findSpatialArmChains ──────────────────────────────────────────────

function _findSpatialArmChains(
  skel: Skeleton,
  chestIdx: number,
  spatialData: SkeletonSpatialData,
  _landmarks: SpatialLandmarks | null,
  leftLeg: number[] | null,
  rightLeg: number[] | null,
): [number[] | null, number[] | null, number | null, number | null] {
  const chest = skel.bone(chestIdx);

  const getX = (idx: number) => getRawX(skel, idx, spatialData.lateralAxis);

  let leftIsLowerX = true;
  if (leftLeg && leftLeg.length > 0 && rightLeg && rightLeg.length > 0) {
    leftIsLowerX = getX(leftLeg[0]) < getX(rightLeg[0]);
  }

  const candidatePairs: [number, number, number][] = [];

  const nonAuxChildren: number[] = [];
  const chestVert = spatialData.getVerticalPosition(chestIdx);
  const _MIN_ARM_SUBTREE = 4;

  for (const c of chest.children) {
    const cb = skel.bone(c);
    if (cb.auxiliary) continue;
    const cVert = spatialData.getVerticalPosition(c);
    const cX = Math.abs(getX(c));
    if (cVert > chestVert && cX < spatialData.totalHeight * 0.005) continue;
    if (cb.subtreeSize < _MIN_ARM_SUBTREE) continue;
    nonAuxChildren.push(c);
  }

  for (let i = 0; i < nonAuxChildren.length; i++) {
    const idxA = nonAuxChildren[i];
    const xA = getX(idxA);
    for (let j = i + 1; j < nonAuxChildren.length; j++) {
      const idxB = nonAuxChildren[j];
      const xB = getX(idxB);
      if (xA * xB >= 0) continue;
      const absA = Math.abs(xA);
      const absB = Math.abs(xB);
      if (Math.max(absA, absB) > 1e-6) {
        const ratio = Math.min(absA, absB) / Math.max(absA, absB);
        if (ratio < 0.3) continue;
      }
      const centerDist = (absA + absB) / 2.0;
      let lr: [number, number];
      if (leftIsLowerX) {
        lr = xA < xB ? [idxA, idxB] : [idxB, idxA];
      } else {
        lr = xA > xB ? [idxA, idxB] : [idxB, idxA];
      }
      candidatePairs.push([lr[0], lr[1], centerDist]);
    }
  }

  candidatePairs.sort((a, b) => a[2] - b[2]);

  let leftRoot: number | null = null;
  let rightRoot: number | null = null;

  if (candidatePairs.length > 0) {
    leftRoot = candidatePairs[0][0];
    rightRoot = candidatePairs[0][1];
  }

  if (leftRoot === null || rightRoot === null) {
    const childXPositions: [number, number][] = [];
    for (const childIdx of chest.children) {
      if (skel.bone(childIdx).auxiliary) continue;
      childXPositions.push([childIdx, getX(childIdx)]);
    }
    childXPositions.sort((a, b) => a[1] - b[1]);

    if (childXPositions.length >= 2) {
      const lowestX = childXPositions[0];
      const highestX = childXPositions[childXPositions.length - 1];
      if (leftIsLowerX) {
        if (leftRoot === null) leftRoot = lowestX[0];
        if (rightRoot === null) rightRoot = highestX[0];
      } else {
        if (leftRoot === null) leftRoot = highestX[0];
        if (rightRoot === null) rightRoot = lowestX[0];
      }
    }
  }

  if ((leftRoot === null || rightRoot === null) && chest.children.length >= 2) {
    const nacFiltered = chest.children.filter(c => !skel.bone(c).auxiliary);
    if (nacFiltered.length >= 2) {
      const sortedChildren = [...nacFiltered].sort((a, b) => getX(a) - getX(b));
      if (leftRoot === null && rightRoot === null) {
        if (leftIsLowerX) {
          leftRoot = sortedChildren[0];
          rightRoot = sortedChildren[sortedChildren.length - 1];
        } else {
          leftRoot = sortedChildren[sortedChildren.length - 1];
          rightRoot = sortedChildren[0];
        }
      }
    }
  }

  if (leftRoot === null || rightRoot === null) {
    return [null, null, null, null];
  }

  const leftX = getX(leftRoot);
  const rightX = getX(rightRoot);
  if (leftIsLowerX && leftX > rightX) {
    [leftRoot, rightRoot] = [rightRoot, leftRoot];
  } else if (!leftIsLowerX && leftX < rightX) {
    [leftRoot, rightRoot] = [rightRoot, leftRoot];
  }

  const leftDir: 'left' | 'right' = leftIsLowerX ? 'left' : 'right';
  const rightDir: 'left' | 'right' = leftIsLowerX ? 'right' : 'left';

  const leftArm = findLateralSpreadChain(skel, leftRoot, spatialData, leftDir);
  const rightArm = findLateralSpreadChain(skel, rightRoot, spatialData, rightDir);

  if (leftArm.length < 2 || rightArm.length < 2) {
    return [null, null, null, null];
  }

  const leftSpread = getLateralSpread(spatialData, leftArm);
  const rightSpread = getLateralSpread(spatialData, rightArm);

  if (leftSpread < 0.15 || rightSpread < 0.15) {
    return [null, null, null, null];
  }

  let leftHand: number | null = null;
  let rightHand: number | null = null;

  if (leftArm.length > 0) {
    const lastBone = leftArm[leftArm.length - 1];
    const bp = spatialData.getBranchingPoint(lastBone);
    if (bp && bp.childCount >= 3) leftHand = lastBone;
  }

  if (rightArm.length > 0) {
    const lastBone = rightArm[rightArm.length - 1];
    const bp = spatialData.getBranchingPoint(lastBone);
    if (bp && bp.childCount >= 3) rightHand = lastBone;
  }

  return [leftArm, rightArm, leftHand, rightHand];
}

// ─── _findSpatialArmRoots ───────────────────────────────────────────────

function _findSpatialArmRoots(
  skel: Skeleton,
  chestIdx: number,
  spatialData: SkeletonSpatialData,
  lateralAxis: Vector3,
  pelvisPos: Vector3,
): [number[], number[]] {
  const leftRoots: number[] = [];
  const rightRoots: number[] = [];

  if (spatialData === null || chestIdx === null) return [leftRoots, rightRoots];

  const bp = spatialData.getBranchingPoint(chestIdx);
  if (!bp) return [leftRoots, rightRoots];

  for (const [leftIdx, rightIdx] of bp.symmetricPairs) {
    let l = leftIdx;
    let r = rightIdx;

    const leftPos = boneWorldPos(skel, l);
    const rightPos = boneWorldPos(skel, r);

    const leftLateral = new Vector3().subVectors(leftPos, pelvisPos).dot(lateralAxis);
    const rightLateral = new Vector3().subVectors(rightPos, pelvisPos).dot(lateralAxis);

    if (leftLateral * rightLateral >= 0) {
      if (Math.abs(leftLateral) > Math.abs(rightLateral)) {
        [l, r] = [r, l];
      }
    }

    const finalLeftLateral = new Vector3().subVectors(boneWorldPos(skel, l), pelvisPos).dot(lateralAxis);
    if (finalLeftLateral < 0) {
      leftRoots.push(l);
      rightRoots.push(r);
    } else {
      leftRoots.push(r);
      rightRoots.push(l);
    }
  }

  return [leftRoots, rightRoots];
}

// ─── detectArms ─────────────────────────────────────────────────────────

export function detectArms(
  skel: Skeleton,
  spineChain: number[],
  chestIdx: number | null,
  pelvisIdx: number,
  lateralAxis: Vector3,
  leftLeg: number[],
  rightLeg: number[],
  config: RecognizerConfig,
  neckChain: number[] | null = null,
  spatialData: SkeletonSpatialData | null = null,
  landmarks: SpatialLandmarks | null = null,
  useSpatialFallback = false,
): [number[] | null, number[] | null, number] {
  if (useSpatialFallback && spatialData !== null && chestIdx !== null) {
    const [leftArm, rightArm] = _findSpatialArmChains(
      skel, chestIdx, spatialData, landmarks,
      leftLeg, rightLeg,
    );

    if (leftArm !== null && rightArm !== null) {
      const lenA = chainLength(skel, leftArm);
      const lenB = chainLength(skel, rightArm);
      const symmetry = 1.0 - Math.abs(lenA - lenB) / Math.max(lenA, lenB, 0.001);
      return [leftArm, rightArm, symmetry];
    }
  }

  const effectiveChestIdx = chestIdx ?? (spineChain.length > 0 ? spineChain[spineChain.length - 1] : pelvisIdx);
  const chest = skel.bone(effectiveChestIdx);

  const exclude = new Set<number>();
  for (const idx of spineChain) exclude.add(idx);
  if (leftLeg) for (const idx of leftLeg) exclude.add(idx);
  if (rightLeg) for (const idx of rightLeg) exclude.add(idx);
  exclude.add(pelvisIdx);

  const pelvisPos = boneWorldPos(skel, pelvisIdx);

  const spatialArmRoots = new Set<number>();
  if (spatialData !== null && effectiveChestIdx !== null) {
    const [spatialLeft, spatialRight] = _findSpatialArmRoots(
      skel, effectiveChestIdx, spatialData, lateralAxis, pelvisPos,
    );
    for (const idx of spatialLeft) spatialArmRoots.add(idx);
    for (const idx of spatialRight) spatialArmRoots.add(idx);
  }

  const leftArmRoots: number[] = [];
  const rightArmRoots: number[] = [];

  const dotVec = (pos: Vector3, axis: Vector3): number =>
    pos.x * axis.x + pos.y * axis.y + pos.z * axis.z;

  const getSide = (pos: Vector3): number =>
    dotVec(new Vector3().subVectors(pos, pelvisPos), lateralAxis);

  const isArmSkipName = (name: string): boolean =>
    isNeckLikeName(name) ||
    isHeadLikeName(name) ||
    isPelvisLikeName(name) ||
    isLegLikeName(name);
  const isSpineSkipName = (name: string): boolean =>
    isSpineLikeName(name) ||
    isNeckLikeName(name) ||
    isHeadLikeName(name);
  const isNeckSkipName = (name: string): boolean =>
    isNeckLikeName(name) || isHeadLikeName(name);

  function classifyChild(childIdx: number, boneSkel: Skeleton): void {
    const childBone = boneSkel.bone(childIdx);
    if (childBone.isLeft) {
      leftArmRoots.push(childIdx);
    } else if (childBone.isRight) {
      rightArmRoots.push(childIdx);
    } else {
      let spatialSide: 'Left' | 'Right' | null = null;
      if (spatialData) spatialSide = spatialData.getSideFromSpatial(childIdx, boneSkel);

      if (spatialSide === 'Left') {
        leftArmRoots.push(childIdx);
      } else if (spatialSide === 'Right') {
        rightArmRoots.push(childIdx);
      } else {
        const childPos = boneWorldPos(boneSkel, childIdx);
        const side = getSide(childPos);
        if (side < 0) leftArmRoots.push(childIdx);
        else rightArmRoots.push(childIdx);
      }
    }
  }

  for (const child of chest.children) {
    if (exclude.has(child) || skel.bone(child).auxiliary) {
      const childBone = skel.bone(child);
      const nameLower = childBone.name.toLowerCase();
      if (!isClavicleLikeName(nameLower)) continue;

      if (childBone.isLeft) {
        leftArmRoots.push(child);
      } else if (childBone.isRight) {
        rightArmRoots.push(child);
      } else {
        const candidateChildren = childBone.children.filter(c => !skel.bone(c).auxiliary);
        if (candidateChildren.length === 0) continue;
        for (const gc of candidateChildren) {
          classifyChild(gc, skel);
        }
      }
      continue;
    }

    const childBone = skel.bone(child);
    const nameLower = childBone.name.toLowerCase();

    if (isArmSkipName(nameLower)) continue;

    if (childBone.isLeft) {
      leftArmRoots.push(child);
    } else if (childBone.isRight) {
      rightArmRoots.push(child);
    } else {
      if (!useSpatialFallback) {
        if (!isArmLikeName(nameLower)) continue;
      }

      let spatialSide: 'Left' | 'Right' | null = null;
      if (spatialData) spatialSide = spatialData.getSideFromSpatial(child, skel);

      if (spatialSide === 'Left') {
        leftArmRoots.push(child);
      } else if (spatialSide === 'Right') {
        rightArmRoots.push(child);
      } else {
        const childPos = boneWorldPos(skel, child);
        const side = getSide(childPos);
        if (side < 0) leftArmRoots.push(child);
        else rightArmRoots.push(child);
      }
    }
  }

  if ((!leftArmRoots.length || !rightArmRoots.length) && spineChain.length > 0) {
    for (const spineIdx of spineChain) {
      if (spineIdx === effectiveChestIdx) continue;
      const spineBone = skel.bone(spineIdx);
      for (const child of spineBone.children) {
        if (exclude.has(child) || leftArmRoots.includes(child) || rightArmRoots.includes(child)) continue;
        const childBone = skel.bone(child);
        if (childBone.auxiliary) continue;
        const nameLower = childBone.name.toLowerCase();

        if (isSpineSkipName(nameLower)) continue;
        if (!useSpatialFallback) {
          if (!isArmLikeName(nameLower)) continue;
        }

        if (childBone.isLeft) {
          if (!leftArmRoots.length) leftArmRoots.push(child);
        } else if (childBone.isRight) {
          if (!rightArmRoots.length) rightArmRoots.push(child);
        } else {
          let spatialSide: 'Left' | 'Right' | null = null;
          if (spatialData) spatialSide = spatialData.getSideFromSpatial(child, skel);

          if (spatialSide === 'Left' && !leftArmRoots.length) {
            leftArmRoots.push(child);
          } else if (spatialSide === 'Right' && !rightArmRoots.length) {
            rightArmRoots.push(child);
          } else {
            const childPos = boneWorldPos(skel, child);
            const side = getSide(childPos);
            if (side < 0 && !leftArmRoots.length) leftArmRoots.push(child);
            else if (side >= 0 && !rightArmRoots.length) rightArmRoots.push(child);
          }
        }
      }
    }
  }

  if ((!leftArmRoots.length || !rightArmRoots.length) && neckChain) {
    for (const neckIdx of neckChain) {
      const neckBone = skel.bone(neckIdx);
      for (const child of neckBone.children) {
        if (exclude.has(child) || leftArmRoots.includes(child) || rightArmRoots.includes(child)) continue;
        const childBone = skel.bone(child);
        if (childBone.auxiliary) continue;
        const nameLower = childBone.name.toLowerCase();

        if (isNeckSkipName(nameLower)) continue;
        if (!useSpatialFallback) {
          if (!isArmLikeName(nameLower)) continue;
        }

        if (childBone.isLeft) {
          if (!leftArmRoots.length) leftArmRoots.push(child);
        } else if (childBone.isRight) {
          if (!rightArmRoots.length) rightArmRoots.push(child);
        } else {
          let spatialSide: 'Left' | 'Right' | null = null;
          if (spatialData) spatialSide = spatialData.getSideFromSpatial(child, skel);

          if (spatialSide === 'Left' && !leftArmRoots.length) {
            leftArmRoots.push(child);
          } else if (spatialSide === 'Right' && !rightArmRoots.length) {
            rightArmRoots.push(child);
          } else {
            const childPos = boneWorldPos(skel, child);
            const side = getSide(childPos);
            if (side < 0 && !leftArmRoots.length) leftArmRoots.push(child);
            else if (side >= 0 && !rightArmRoots.length) rightArmRoots.push(child);
          }
        }
      }
    }
  }

  if (!leftArmRoots.length && !rightArmRoots.length) {
    return [null, null, 0.0];
  }

  function isHandLike(name: string): boolean {
    const lowered = name.toLowerCase();
    if (isTerminalHelperLikeName(lowered)) return false;
    const fingerPatterns = [
      'handthumb', 'handindex', 'handmiddle', 'handring', 'handpinky', 'handfinger',
      'hand_thumb', 'hand_index', 'hand_middle', 'hand_ring', 'hand_pinky', 'hand_finger',
    ];
    if (fingerPatterns.some(fp => lowered.includes(fp))) return false;
    return ['hand', 'wrist', 'wrise', 'cannon', '\u624b\u9996'].some(t => lowered.includes(t));
  }

  function isHandLikeSpatial(boneIdx: number): boolean {
    const bone = skel.bone(boneIdx);
    const nonAuxChildren = bone.children.filter((c) => !skel.bone(c).auxiliary);
    if (nonAuxChildren.length < 3) return false;

    let chainChildren = 0;
    for (const childIdx of nonAuxChildren) {
      if (skel.bone(childIdx).children.some((c) => !skel.bone(c).auxiliary)) {
        chainChildren += 1;
      }
    }

    return chainChildren >= 3;
  }

  function extractArmPaths(startIdx: number): number[][] {
    const paths: number[][] = [];

    function getEffectiveChildren(idx: number): number[] {
      const bone = skel.bone(idx);
      const result: number[] = [];
      for (const c of bone.children) {
        const child = skel.bone(c);
        if (child.auxiliary && !isStructuralArmAuxiliaryName(child.name)) {
          result.push(...getEffectiveChildren(c));
        } else {
          result.push(c);
        }
      }
      return result;
    }

    function dfs(idx: number, current: number[]): void {
      const bone = skel.bone(idx);
      if (bone.auxiliary && !isStructuralArmAuxiliaryName(bone.name)) return;
      current.push(idx);
      const children = getEffectiveChildren(idx);
      if (children.length === 0) {
        paths.push([...current]);
      } else {
        for (const child of children) dfs(child, current);
      }
      current.pop();
    }

    dfs(startIdx, []);
    return paths;
  }

  function getBestArmPath(roots: number[]): number[] | null {
    let bestPath: number[] | null = null;
    let bestScore = -1;
    for (const root of roots) {
      const paths = extractArmPaths(root);
      for (const path of paths) {
        let score = path.length;
        for (const idx of path) {
          if (isHandLike(skel.bone(idx).name)) score += 100;
          else if (isHandLikeSpatial(idx)) score += 80;
        }
        if (score > bestScore) {
          bestScore = score;
          bestPath = path;
        }
      }
    }
    return bestPath;
  }

  let leftArm = getBestArmPath(leftArmRoots);
  let rightArm = getBestArmPath(rightArmRoots);

  function trimToHand(path: number[] | null): number[] | null {
    if (!path) return path;
    let lastHandIdx: number | null = null;
    for (let i = 0; i < path.length; i++) {
      if (isHandLike(skel.bone(path[i]).name)) lastHandIdx = i;
    }
    if (lastHandIdx !== null) return path.slice(0, lastHandIdx + 1);
    for (let i = 0; i < path.length; i++) {
      if (isHandLikeSpatial(path[i])) return path.slice(0, i + 1);
    }
    return path;
  }

  leftArm = trimToHand(leftArm);
  rightArm = trimToHand(rightArm);

  let symmetry = 0.0;
  if (leftArm && rightArm) {
    const lenA = chainLength(skel, leftArm);
    const lenB = chainLength(skel, rightArm);
    symmetry = 1.0 - Math.abs(lenA - lenB) / Math.max(lenA, lenB, 0.001);
  }

  return [leftArm, rightArm, symmetry];
}

// ─── extractClavicleFromArm ─────────────────────────────────────────────

export function extractClavicleFromArm(
  skel: Skeleton,
  armChain: number[],
  spatialData: SkeletonSpatialData | null = null,
): [number | null, number[]] {
  if (armChain.length < 3) return [null, armChain];

  const firstBone = skel.bone(armChain[0]);
  const secondBone = skel.bone(armChain[1]);

  const isClavicleByName = isClavicleLikeName(firstBone.name);

  const needsLongChain = !isClavicleByName && armChain.length < 4;
  if (needsLongChain) return [null, armChain];

  const isClavicleByLength = firstBone.length < 0.6 * secondBone.length;

  let isClavicleByGeometry = false;
  if (spatialData !== null) {
    isClavicleByGeometry = isClavickleLike(skel, armChain[0], spatialData, armChain[1]);

    const firstE = firstBone.restWorld.elements;
    const secondE = secondBone.restWorld.elements;
    const firstPos = new Vector3(firstE[12], firstE[13], firstE[14]);
    const secondPos = new Vector3(secondE[12], secondE[13], secondE[14]);

    const firstAbsX = Math.abs(firstPos.dot(spatialData.lateralAxis));
    const secondAbsX = Math.abs(secondPos.dot(spatialData.lateralAxis));
    const outwardMovement = secondAbsX - firstAbsX;

    const extent = new Vector3().subVectors(spatialData.maxBounds, spatialData.minBounds);
    const lateralExtent = dotAbs(extent, spatialData.lateralAxis);
    const minOutwardMovement = lateralExtent * 0.05;

    if (firstAbsX < secondAbsX && outwardMovement > minOutwardMovement) {
      isClavicleByGeometry = true;
    }
  }

  if (isClavicleByName || isClavicleByLength || isClavicleByGeometry) {
    return [armChain[0], armChain.slice(1)];
  }

  return [null, armChain];
}

// ─── ExtraArmCandidate ──────────────────────────────────────────────────

export interface ExtraArmCandidate {
  side: string;
  clavicleIdx: number | null;
  armChain: number[];
  spineParentIdx: number;
}

// ─── Hand-like helpers ──────────────────────────────────────────────────

const _FINGER_PATTERNS = [
  'handthumb', 'handindex', 'handmiddle', 'handring', 'handpinky',
  'handfinger', 'hand_thumb', 'hand_index', 'hand_middle',
  'hand_ring', 'hand_pinky', 'hand_finger',
];

function _isHandLike(name: string): boolean {
  const lowered = name.toLowerCase();
  if (_FINGER_PATTERNS.some(fp => lowered.includes(fp))) return false;
  return ['hand', 'wrist', 'palm', '\u624b\u9996'].some(t => lowered.includes(t));
}

function _isHandLikeSpatial(skel: Skeleton, boneIdx: number): boolean {
  const bone = skel.bone(boneIdx);
  const nonAuxChildren = bone.children.filter(c => !skel.bone(c).auxiliary);
  if (nonAuxChildren.length < 3) return false;
  let chainChildren = 0;
  for (const c of nonAuxChildren) {
    if (skel.bone(c).children.length > 0) chainChildren++;
  }
  return chainChildren >= 3;
}

// ─── _traceArmLikeChain ────────────────────────────────────────────────

function _traceArmLikeChain(
  skel: Skeleton,
  startIdx: number,
  claimed: Set<number>,
): number[] {
  function effectiveChildren(idx: number): number[] {
    const bone = skel.bone(idx);
    const result: number[] = [];
    for (const c of bone.children) {
      if (claimed.has(c)) continue;
      if (skel.bone(c).auxiliary) {
        result.push(...effectiveChildren(c));
      } else {
        result.push(c);
      }
    }
    return result;
  }

  const paths: number[][] = [];

  function dfs(idx: number, current: number[]): void {
    if (skel.bone(idx).auxiliary) return;
    current.push(idx);
    const children = effectiveChildren(idx);
    if (children.length === 0) {
      paths.push([...current]);
    } else {
      for (const child of children) dfs(child, current);
    }
    current.pop();
  }

  dfs(startIdx, []);

  let bestPath: number[] | null = null;
  let bestScore = -1;
  for (const path of paths) {
    let score = path.length;
    for (const idx of path) {
      if (_isHandLike(skel.bone(idx).name)) score += 100;
      else if (_isHandLikeSpatial(skel, idx)) score += 50;
    }
    if (score > bestScore) {
      bestScore = score;
      bestPath = path;
    }
  }

  if (!bestPath) return [];

  let lastHandPos: number | null = null;
  for (let i = 0; i < bestPath.length; i++) {
    if (_isHandLike(skel.bone(bestPath[i]).name)) lastHandPos = i;
  }
  if (lastHandPos === null) {
    for (let i = 0; i < bestPath.length; i++) {
      if (_isHandLikeSpatial(skel, bestPath[i])) {
        lastHandPos = i;
        break;
      }
    }
  }
  if (lastHandPos !== null) {
    bestPath = bestPath.slice(0, lastHandPos + 1);
  }

  return bestPath;
}

function _pathBetweenAncestorAndDescendant(
  skel: Skeleton,
  ancestorIdx: number,
  descendantIdx: number,
): number[] | null {
  const path: number[] = [];
  let current: number | null = descendantIdx;

  while (current !== null) {
    path.unshift(current);
    if (current === ancestorIdx) return path;
    current = skel.bone(current).parent;
  }

  return null;
}

function _expandInlineArmSegments(skel: Skeleton, chain: number[]): number[] {
  if (chain.length <= 1) return [...chain];

  const expanded = [chain[0]];
  for (let i = 1; i < chain.length; i += 1) {
    const segment = _pathBetweenAncestorAndDescendant(
      skel,
      chain[i - 1],
      chain[i],
    );
    if (!segment) {
      expanded.push(chain[i]);
      continue;
    }
    expanded.push(...segment.slice(1));
  }

  return expanded;
}

// ─── _isArmLikeBranch ──────────────────────────────────────────────────

function _isArmLikeBranch(
  skel: Skeleton,
  chain: number[],
  torsoPos: Vector3,
  lateralAxis: Vector3,
  lateralExtent: number,
  upAxis: Vector3,
): boolean {
  if (chain.length < 3) return false;

  const handPos = boneWorldPos(skel, chain[chain.length - 1]);
  const startPos = boneWorldPos(skel, chain[0]);
  const delta = new Vector3().subVectors(handPos, torsoPos);
  const lateralDisp = Math.abs(delta.dot(lateralAxis));

  // Use computed upAxis instead of hardcoded Y — handles prone / Z-up rigs
  const verticalDispFromTorso = Math.abs(delta.dot(upAxis));

  // Chain-intrinsic displacement: immune to torsoPos at origin
  const chainDelta = new Vector3().subVectors(handPos, startPos);
  const verticalDispIntrinsic = Math.abs(chainDelta.dot(upAxis));

  const verticalDisp = Math.max(verticalDispFromTorso, verticalDispIntrinsic);

  if (lateralDisp < verticalDisp * 0.5) return false;
  if (lateralDisp < 0.10 * lateralExtent) return false;

  return true;
}

// ─── _determineExtraArmSide ────────────────────────────────────────────

function _determineExtraArmSide(
  skel: Skeleton,
  chain: number[],
  lateralAxis: Vector3,
  pelvisPos: Vector3,
): string | null {
  if (chain.length === 0) return null;

  const handPos = boneWorldPos(skel, chain[chain.length - 1]);
  const offset = new Vector3().subVectors(handPos, pelvisPos).dot(lateralAxis);
  let spatialSide: string | null = null;
  if (Math.abs(offset) > 1e-6) {
    spatialSide = offset < 0 ? 'Left' : 'Right';
  }

  let namingSide: string | null = null;
  for (const idx of chain) {
    const b = skel.bone(idx);
    if (b.isLeft) { namingSide = 'Left'; break; }
    if (b.isRight) { namingSide = 'Right'; break; }
  }

  return namingSide ?? spatialSide;
}

// ─── _splitExtraArmClavicle ────────────────────────────────────────────

function _splitExtraArmClavicle(
  skel: Skeleton,
  chain: number[],
  lateralAxis: Vector3 | null = null,
  lateralExtent = 0.0,
): [number | null, number[]] {
  const clavicleTerms = ROLE_TERMS['Clavicle'] ?? [];

  if (chain.length < 4) return [null, chain];

  const firstBone = skel.bone(chain[0]);
  const secondBone = skel.bone(chain[1]);

  const firstName = firstBone.name.toLowerCase();
  if (clavicleTerms.some(t => firstName.includes(t))) {
    return [chain[0], chain.slice(1)];
  }

  let isClavicleByLength = false;
  if (firstBone.length > 1e-8 && secondBone.length > 1e-8) {
    if (firstBone.length < 0.6 * secondBone.length) {
      isClavicleByLength = true;
    }
  }

  let isClavicleByGeometry = false;
  if (lateralAxis !== null && lateralExtent > 1e-6) {
    const firstE = firstBone.restWorld.elements;
    const secondE = secondBone.restWorld.elements;
    const firstPos = new Vector3(firstE[12], firstE[13], firstE[14]);
    const secondPos = new Vector3(secondE[12], secondE[13], secondE[14]);
    const firstAbsX = Math.abs(firstPos.dot(lateralAxis));
    const secondAbsX = Math.abs(secondPos.dot(lateralAxis));
    const outwardMovement = secondAbsX - firstAbsX;
    const minOutward = lateralExtent * 0.05;
    if (firstAbsX < secondAbsX && outwardMovement > minOutward) {
      isClavicleByGeometry = true;
    }
  }

  if (isClavicleByLength || isClavicleByGeometry) {
    return [chain[0], chain.slice(1)];
  }

  return [null, chain];
}

// ─── findExtraArmCandidates ─────────────────────────────────────────────

export function findExtraArmCandidates(
  skel: Skeleton,
  torsoIndices: number[],
  claimedIndices: Set<number>,
  lateralAxis: Vector3,
  pelvisPos: Vector3,
  lateralExtent: number,
  upAxis: Vector3 = new Vector3(0, 1, 0),
): ExtraArmCandidate[] {
  const candidates: ExtraArmCandidate[] = [];
  const torsoSet = new Set(torsoIndices);

  for (const torsoIdx of torsoIndices) {
    const torsoBone = skel.bone(torsoIdx);
    const torsoPos = boneWorldPos(skel, torsoIdx);

    for (const childIdx of torsoBone.children) {
      if (torsoSet.has(childIdx)) continue;
      if (claimedIndices.has(childIdx)) continue;
      if (skel.bone(childIdx).auxiliary) continue;

      const fullChain = _traceArmLikeChain(skel, childIdx, claimedIndices);
      if (fullChain.length < 3) continue;

      if (!_isArmLikeBranch(skel, fullChain, torsoPos, lateralAxis, lateralExtent, upAxis)) {
        continue;
      }

      const side = _determineExtraArmSide(skel, fullChain, lateralAxis, pelvisPos);
      if (side === null) continue;

      const [clavicleIdx, armChain] = _splitExtraArmClavicle(
        skel, fullChain, lateralAxis, lateralExtent,
      );

      const expandedArmChain = _expandInlineArmSegments(skel, armChain);
      if (expandedArmChain.length < 3) continue;

      candidates.push({
        side,
        clavicleIdx,
        armChain: expandedArmChain,
        spineParentIdx: torsoIdx,
      });
    }
  }

  candidates.sort((a, b) => {
    const aY = skel.bone(a.spineParentIdx).restWorld.elements[13];
    const bY = skel.bone(b.spineParentIdx).restWorld.elements[13];
    return bY - aY;
  });

  return candidates;
}
